import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    CheckSquare,
    Clock,
    AlertOctagon,
    Building2,
    PackageX,
    Sparkles,
    ArrowUpRight,
    Activity,
} from "lucide-react";
import api from "@/lib/api";
import KPICard from "@/components/KPICard";
import StateBadge from "@/components/StateBadge";
import {
    PROPERTY_STATE_LABELS,
    TASK_STATE_LABELS,
    INCIDENT_STATE_LABELS,
    PRIORITY_LABELS,
    TASK_TYPE_LABELS,
    INCIDENT_TYPE_LABELS,
} from "@/lib/dictionaries";
import { useAuth } from "@/contexts/AuthContext";

export default function Dashboard() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        api.get("/dashboard")
            .then((r) => {
                if (active) setData(r.data);
            })
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, []);

    if (loading || !data) {
        return (
            <div className="space-y-6" data-testid="dashboard-loading">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="zentra-skeleton h-28" />
                    ))}
                </div>
                <div className="zentra-skeleton h-72" />
            </div>
        );
    }

    const k = data.kpis;

    return (
        <div className="space-y-8" data-testid="dashboard">
            {/* Header */}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Centro operativo · {new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
                    </p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                        Hola, {user?.name?.split(" ")[0] || "Operador"}.
                    </h2>
                    <p className="text-sm text-zentra-muted mt-1">
                        Esto es lo que está pasando hoy en tu operativa.
                    </p>
                </div>
                <Link to="/limpiezas" className="zentra-btn-primary" data-testid="dashboard-cta-tasks">
                    Ver planificación
                    <ArrowUpRight size={14} />
                </Link>
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-grid">
                <KPICard
                    title="Tareas hoy"
                    value={k.tasks_today}
                    suffix="programadas"
                    icon={CheckSquare}
                    hint={`${k.tasks_completed_today} completadas`}
                    trend={k.tasks_completed_today >= k.tasks_today / 2 ? "up" : "down"}
                    accent
                    testId="kpi-tasks-today"
                />
                <KPICard
                    title="Retrasadas"
                    value={k.tasks_delayed}
                    suffix={k.tasks_delayed === 1 ? "tarea" : "tareas"}
                    icon={Clock}
                    hint={k.tasks_delayed > 0 ? "Requieren reasignación" : "Todo en plazo"}
                    testId="kpi-tasks-delayed"
                />
                <KPICard
                    title="Incidencias abiertas"
                    value={k.incidents_open}
                    suffix="activas"
                    icon={AlertOctagon}
                    hint="Crítica + alta priorizadas"
                    testId="kpi-incidents"
                />
                <KPICard
                    title="Propiedades"
                    value={k.active_properties}
                    suffix="activas"
                    icon={Building2}
                    hint={`Ocupación ${k.occupancy_ratio}%`}
                    testId="kpi-properties"
                />
                <KPICard
                    title="Stock crítico"
                    value={k.critical_stock}
                    suffix="artículos"
                    icon={PackageX}
                    hint="Reponer antes de 24h"
                    testId="kpi-stock"
                />
                <KPICard
                    title="Productividad"
                    value={Math.round((k.tasks_completed_today / Math.max(1, k.tasks_today)) * 100)}
                    suffix="%"
                    icon={Activity}
                    hint="Ratio completadas/programadas"
                    accent
                    testId="kpi-productivity"
                />
                <KPICard
                    title="Apartamentos limpios"
                    value={data.properties_by_state?.cleaned || 0}
                    suffix="listos"
                    icon={Sparkles}
                    accent
                    testId="kpi-cleaned"
                />
                <KPICard
                    title="Bloqueados / mant."
                    value={(data.properties_by_state?.blocked || 0) + (data.properties_by_state?.maintenance || 0)}
                    icon={AlertOctagon}
                    hint="Fuera de operación"
                    testId="kpi-blocked"
                />
            </div>

            {/* Two columns: recent tasks + recent incidents + property states */}
            <div className="grid lg:grid-cols-3 gap-6">
                {/* Property states */}
                <div className="zentra-card-static p-5 lg:col-span-1" data-testid="property-states">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display font-semibold text-zentra-ink">Estado de propiedades</h3>
                        <Link to="/propiedades" className="text-xs font-medium text-zentra-accent-strong hover:underline">
                            Ver todas
                        </Link>
                    </div>
                    <div className="space-y-2">
                        {Object.entries(data.properties_by_state || {})
                            .filter(([, v]) => v > 0)
                            .sort((a, b) => b[1] - a[1])
                            .map(([state, count]) => (
                                <div key={state} className="flex items-center justify-between py-1.5 border-b border-zentra-border last:border-0">
                                    <StateBadge value={state} dict={PROPERTY_STATE_LABELS} />
                                    <span className="font-display font-semibold text-zentra-ink">{count}</span>
                                </div>
                            ))}
                    </div>
                </div>

                {/* Recent tasks */}
                <div className="zentra-card-static p-5 lg:col-span-1" data-testid="recent-tasks">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display font-semibold text-zentra-ink">Tareas recientes</h3>
                        <Link to="/limpiezas" className="text-xs font-medium text-zentra-accent-strong hover:underline">
                            Ver todas
                        </Link>
                    </div>
                    <ul className="divide-y divide-zentra-border">
                        {(data.recent_tasks || []).map((t) => (
                            <li key={t.id} className="py-3 flex flex-col gap-1.5">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-medium text-sm text-zentra-ink">
                                        {TASK_TYPE_LABELS[t.type] || t.type}
                                    </span>
                                    <StateBadge value={t.state} dict={TASK_STATE_LABELS} />
                                </div>
                                <div className="flex items-center justify-between text-xs text-zentra-muted">
                                    <span>{t.scheduled_date}</span>
                                    <StateBadge value={t.priority} dict={PRIORITY_LABELS} />
                                </div>
                            </li>
                        ))}
                        {(!data.recent_tasks || data.recent_tasks.length === 0) && (
                            <p className="text-sm text-zentra-muted py-4">Aún no hay tareas registradas.</p>
                        )}
                    </ul>
                </div>

                {/* Recent incidents */}
                <div className="zentra-card-static p-5 lg:col-span-1" data-testid="recent-incidents">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display font-semibold text-zentra-ink">Incidencias recientes</h3>
                        <Link to="/incidencias" className="text-xs font-medium text-zentra-accent-strong hover:underline">
                            Ver todas
                        </Link>
                    </div>
                    <ul className="divide-y divide-zentra-border">
                        {(data.recent_incidents || []).map((inc) => (
                            <li key={inc.id} className="py-3 flex flex-col gap-1.5">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-medium text-sm text-zentra-ink truncate">{inc.title}</span>
                                    {inc.priority === "critical" && <span className="w-2 h-2 rounded-full bg-red-600 zentra-pulse" />}
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-zentra-muted">{INCIDENT_TYPE_LABELS[inc.type] || inc.type}</span>
                                    <StateBadge value={inc.state} dict={INCIDENT_STATE_LABELS} />
                                </div>
                            </li>
                        ))}
                        {(!data.recent_incidents || data.recent_incidents.length === 0) && (
                            <p className="text-sm text-zentra-muted py-4">Sin incidencias recientes.</p>
                        )}
                    </ul>
                </div>
            </div>

            {/* Critical stock */}
            {data.critical_stock_items?.length > 0 && (
                <div className="zentra-card-static p-5" data-testid="critical-stock">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="font-display font-semibold text-zentra-ink">Stock crítico</h3>
                            <p className="text-xs text-zentra-muted mt-0.5">
                                Por debajo del mínimo de seguridad
                            </p>
                        </div>
                        <Link to="/almacen" className="text-xs font-medium text-zentra-accent-strong hover:underline">
                            Reponer
                        </Link>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {data.critical_stock_items.map((it) => (
                            <div
                                key={it.id}
                                className="flex items-center justify-between p-3 rounded-md bg-red-50 border border-red-100"
                            >
                                <div className="min-w-0">
                                    <p className="font-medium text-sm text-zentra-ink truncate">{it.name}</p>
                                    <p className="text-[11px] text-zentra-muted font-mono-zentra">{it.sku}</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-display font-semibold text-red-700">{it.stock}</p>
                                    <p className="text-[10px] uppercase tracking-wider text-zentra-muted">
                                        min {it.min_stock}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
